import { Link } from "react-router-dom";

const FEATURES = [
  {
    icon: "ri-heart-pulse-line",
    title: "Contract Health Score",
    desc: "Every contract gets an automatic score based on expiry proximity, documentation status, and supplier performance — so at-risk contracts surface themselves.",
  },
  {
    icon: "ri-flow-chart",
    title: "Renewal Workflow Engine",
    desc: "A structured SCM → Management → CFO/HOD approval flow for every renewal decision, with comments and documents attached at each stage.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Supplier Compliance & Registration",
    desc: "Track B-BBEE status, tax clearance validity, CSD numbers, and vendor numbers per department — all linked directly to the contracts they belong to.",
  },
  {
    icon: "ri-star-line",
    title: "SLA Matrix & Performance Reviews",
    desc: "Quarterly supplier scoring across quality, timeliness, communication, value for money, and compliance — with a full ratings history.",
  },
  {
    icon: "ri-history-line",
    title: "Full Audit Trail",
    desc: "Every status change, renewal decision, and document upload is logged against the contract, with who did it and when.",
  },
  {
    icon: "ri-building-2-line",
    title: "Multi-Tenant by Design",
    desc: "Your organisation's contracts, suppliers, and users are fully isolated from every other SupplyGuard customer, automatically.",
  },
];

const TRUST_ITEMS = [
  {
    icon: "ri-lock-2-line",
    title: "Your data, isolated",
    desc: "Each organisation's contracts and suppliers are fully separated at the database level, always.",
  },
  {
    icon: "ri-flag-line",
    title: "Built for SA compliance",
    desc: "B-BBEE, tax clearance, CSD number — the exact fields South African procurement teams need to track.",
  },
  {
    icon: "ri-bank-card-line",
    title: "Secure billing",
    desc: "Payments are processed via PayFast, South Africa's trusted payment gateway.",
  },
  {
    icon: "ri-time-line",
    title: "14-day free trial",
    desc: "Try the full Professional plan free for 14 days. No credit card required to start.",
  },
];

export default function MarketingHome() {
  return (
    <div className="min-h-screen bg-background-100">
      {/* Nav */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-background-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <img src="/logo.svg" alt="SupplyGuard" className="h-9 w-auto" />
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-foreground-600 text-sm font-medium hover:text-foreground-800 transition-colors">Features</a>
            <a href="#pricing" className="text-foreground-600 text-sm font-medium hover:text-foreground-800 transition-colors">Pricing</a>
          </nav>
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-foreground-600 text-sm font-medium hover:text-foreground-800 transition-colors">Sign in</Link>
            <Link to="/register" className="h-10 px-4 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-lg flex items-center transition-colors">
              Get Started
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-20 lg:pt-24 lg:pb-28 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-flex items-center gap-1.5 bg-primary-50 text-primary-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-5">
            <i className="ri-map-pin-line"></i> Built for South African Government &amp; Parastatal Procurement
          </span>
          <h1 className="text-foreground-900 text-4xl sm:text-5xl font-bold leading-tight mb-5 text-balance">
            Contract &amp; supplier compliance, finally under control.
          </h1>
          <p className="text-foreground-500 text-lg leading-relaxed mb-8 max-w-lg">
            SupplyGuard tracks every contract's health, runs renewals through a proper approval workflow, and keeps supplier compliance data — B-BBEE, tax clearance, CSD — in one place instead of scattered spreadsheets.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/register" className="h-12 px-6 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-xl flex items-center transition-colors">
              Start Free Trial
            </Link>
            <Link to="/pricing" className="h-12 px-6 border border-background-300 text-foreground-700 hover:bg-background-100 text-sm font-semibold rounded-xl flex items-center transition-colors">
              View Pricing
            </Link>
          </div>
          <p className="text-foreground-400 text-xs mt-4">14-day free trial &middot; No credit card required</p>
        </div>

        {/* Dashboard mockup */}
        <div className="relative">
          <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full bg-primary-100 blur-3xl pointer-events-none" />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-background-200 p-5">
            <div className="flex items-center justify-between mb-5">
              <p className="text-foreground-900 font-bold text-sm">Dashboard</p>
              <div className="flex items-center gap-1.5 bg-background-100 rounded-full px-3 py-1">
                <span className="text-foreground-600 text-xs font-medium">This Quarter</span>
                <i className="ri-arrow-down-s-line text-foreground-400 text-xs"></i>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-5">
              <div className="bg-background-50 rounded-xl p-3.5">
                <p className="text-foreground-500 text-[11px] font-medium mb-1.5">Active Contracts</p>
                <p className="text-foreground-900 text-xl font-bold">128</p>
                <p className="text-secondary-600 text-[11px] font-medium mt-1 flex items-center gap-1">
                  <i className="ri-arrow-up-line"></i> +6% this month
                </p>
              </div>
              <div className="bg-background-50 rounded-xl p-3.5">
                <p className="text-foreground-500 text-[11px] font-medium mb-1.5">Expiring &le; 30 Days</p>
                <p className="text-foreground-900 text-xl font-bold">14</p>
                <p className="text-accent-600 text-[11px] font-medium mt-1 flex items-center gap-1">
                  <i className="ri-arrow-up-line"></i> +3 vs last month
                </p>
              </div>
            </div>

            <p className="text-foreground-500 text-[11px] font-semibold uppercase tracking-wider mb-2">Contract Health</p>
            <div className="space-y-2">
              {[
                { name: "Dimension Data (Pty) Ltd", score: 92, label: "Excellent", bg: "bg-secondary-50", text: "text-secondary-700" },
                { name: "Metro Security Services", score: 58, label: "At Risk", bg: "bg-accent-50", text: "text-accent-700" },
                { name: "Rand Water Infrastructure", score: 81, label: "Healthy", bg: "bg-secondary-50", text: "text-secondary-700" },
              ].map((row) => (
                <div key={row.name} className="flex items-center justify-between py-1.5">
                  <span className="text-foreground-700 text-xs font-medium truncate max-w-[140px]">{row.name}</span>
                  <div className="flex items-center gap-2.5">
                    <span className={`${row.bg} ${row.text} text-[10px] font-semibold px-2 py-0.5 rounded-full`}>{row.label}</span>
                    <span className="text-foreground-800 text-xs font-semibold w-6 text-right">{row.score}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden xl:block w-56 ml-auto -mt-4 mr-4 relative bg-white rounded-xl shadow-2xl border border-background-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-foreground-900 text-xs font-bold">Renewal Alert</p>
              <i className="ri-close-line text-foreground-400 text-xs"></i>
            </div>
            <p className="text-foreground-700 text-xs font-medium">Metro Security Services</p>
            <p className="text-accent-600 text-[11px] font-medium mb-3">Expires in 12 days</p>
            <button className="w-full h-7 bg-primary-600 text-white text-[11px] font-semibold rounded-md cursor-default">
              Notify Officer
            </button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-6 py-20 scroll-mt-16">
        <div className="text-center max-w-xl mx-auto mb-12">
          <h2 className="text-foreground-900 text-3xl font-bold mb-3">Everything a procurement team needs</h2>
          <p className="text-foreground-500 text-base">One system for contracts, renewals, and supplier compliance — no more chasing spreadsheets.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-white rounded-2xl border border-background-200 p-6">
              <div className="w-11 h-11 rounded-xl bg-primary-50 flex items-center justify-center mb-4">
                <i className={`${f.icon} text-primary-600 text-xl`}></i>
              </div>
              <h3 className="text-foreground-900 text-base font-semibold mb-2">{f.title}</h3>
              <p className="text-foreground-500 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Trust & Security */}
      <section className="bg-white border-y border-background-200">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center max-w-xl mx-auto mb-12">
            <h2 className="text-foreground-900 text-3xl font-bold mb-3">Trust &amp; security</h2>
            <p className="text-foreground-500 text-base">Built with the realities of South African public-sector procurement in mind.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TRUST_ITEMS.map((t) => (
              <div key={t.title} className="text-center sm:text-left">
                <div className="w-10 h-10 rounded-lg bg-background-100 flex items-center justify-center mb-3 mx-auto sm:mx-0">
                  <i className={`${t.icon} text-foreground-700 text-lg`}></i>
                </div>
                <h3 className="text-foreground-900 text-sm font-semibold mb-1.5">{t.title}</h3>
                <p className="text-foreground-500 text-sm leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing teaser */}
      <section id="pricing" className="max-w-6xl mx-auto px-6 py-20 text-center scroll-mt-16">
        <h2 className="text-foreground-900 text-3xl font-bold mb-3">Simple, transparent pricing</h2>
        <p className="text-foreground-500 text-base max-w-lg mx-auto mb-8">
          Plans start from R1,499/month. Every plan includes a 14-day free trial — no credit card required.
        </p>
        <Link to="/pricing" className="inline-flex h-12 px-6 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-xl items-center transition-colors">
          See full pricing
        </Link>
      </section>

      {/* Final CTA banner */}
      <section className="bg-primary-600">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="text-white text-3xl font-bold mb-3">Ready to bring order to your contract register?</h2>
          <p className="text-primary-200 text-base mb-8">Set up your organisation's workspace in minutes.</p>
          <Link to="/register" className="inline-flex h-12 px-6 bg-white hover:bg-primary-50 text-primary-700 text-sm font-semibold rounded-xl items-center transition-colors">
            Start Free Trial
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-background-200">
        <div className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-1 sm:grid-cols-3 gap-8">
          <div>
            <img src="/logo.svg" alt="SupplyGuard" className="h-8 w-auto mb-3" />
            <p className="text-foreground-400 text-xs leading-relaxed max-w-xs">
              Contract and supplier compliance management for South African government departments and parastatals.
            </p>
          </div>
          <div>
            <p className="text-foreground-700 text-xs font-semibold uppercase tracking-wider mb-3">Product</p>
            <div className="flex flex-col gap-2">
              <a href="#features" className="text-foreground-500 text-sm hover:text-foreground-800 transition-colors">Features</a>
              <a href="#pricing" className="text-foreground-500 text-sm hover:text-foreground-800 transition-colors">Pricing</a>
            </div>
          </div>
          <div>
            <p className="text-foreground-700 text-xs font-semibold uppercase tracking-wider mb-3">Account</p>
            <div className="flex flex-col gap-2">
              <Link to="/login" className="text-foreground-500 text-sm hover:text-foreground-800 transition-colors">Sign in</Link>
              <Link to="/register" className="text-foreground-500 text-sm hover:text-foreground-800 transition-colors">Sign up</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-background-200 px-6 py-5">
          <p className="text-foreground-400 text-xs text-center">
            &copy; {new Date().getFullYear()} SupplyGuard, All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
